"use client"

import { useState, useEffect, useCallback } from "react"
import { useSession } from "next-auth/react"
import {
  ensurePushPermission,
  getBrowserPushSubscription,
  unregisterPushSubscription,
} from "@/lib/push-client"

interface PushState {
  subscribed: boolean
  dismissed: boolean
  loading: boolean
  authenticated: boolean
}

let pushState: PushState = { subscribed: false, dismissed: false, loading: false, authenticated: false }
const listeners = new Set<() => void>()

function setPushState(patch: Partial<PushState>) {
  pushState = { ...pushState, ...patch }
  for (const listener of listeners) listener()
}

export function usePushSetup() {
  const { data: session, status } = useSession()
  const [, forceRender] = useState(0)

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (status === "loading") return
    if (!session?.user?.id) {
      setPushState({ authenticated: false, subscribed: false, dismissed: false })
      return
    }
    setPushState({ authenticated: true })
    let active = true
    const apply = async () => {
      try {
        const subscription = await getBrowserPushSubscription()
        if (active) setPushState({ subscribed: !!subscription })
      } catch {
        /* estado inicial fica desativado */
      }
    }
    apply()
    const onVisible = () => {
      if (!document.hidden) apply()
    }
    const onFocus = () => apply()
    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", onFocus)
    return () => {
      active = false
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", onFocus)
    }
  }, [session?.user?.id, status])

  const setPushEnabled = useCallback(async (value: boolean): Promise<boolean> => {
    setPushState({ loading: true })
    try {
      if (value) {
        const ok = await ensurePushPermission()
        if (!ok) return false
        setPushState({ subscribed: true })
        return true
      }
      await unregisterPushSubscription()
      setPushState({ subscribed: false, dismissed: false })
      return true
    } finally {
      setPushState({ loading: false })
    }
  }, [])

  const dismissBanner = useCallback(() => {
    setPushState({ dismissed: true })
  }, [])

  return {
    subscribed: pushState.subscribed,
    dismissed: pushState.dismissed,
    loading: pushState.loading,
    authenticated: pushState.authenticated,
    setPushEnabled,
    dismissBanner,
  }
}
